import THEME from '../../styles/colors';

// ghost

export const calculateBrightness = (hex: string): number | undefined => {
  if (typeof hex !== 'string') {
    return;
  }
  const { length } = hex.trim();

  if (length !== 3 && length !== 6) {
    return;
  }

  // this will fail if it's length is 3.
  // Not sure why line 11 has the 3 check without logic here to handle it?
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Developed here http://alienryderflex.com/hsp.html
  // returns a value 0-255 255 being white and 0 being black
  return Math.sqrt(r * r * 0.299 + g * g * 0.587 + b * b * 0.114);
};

/* 
// get hex by color name
// Only works on the client 
function calculateHex(color) {
  if (!color) {
    return null;
  }
  const ctx = document.createElement('canvas').getContext();
  ctx.fillStyle = color;
  return ctx.fillStyle;
}
*/

export const prependHashIfNeeded = (color: string): string =>
  ((color ?? ' ')[0] !== '#' ? '#' : '') + color;

interface IColorBrightness {
  slot: number;
  color: string;
  brightness: number;
}

export const getColorBrightness = (colors: string[]): IColorBrightness[] => {
  const colorsByBrightness: IColorBrightness[] = [];
  if (colors && colors.length < 1) {
    return colorsByBrightness;
  }

  for (let i = 0; i < colors.length; i++) {
    const brightness = calculateBrightness(colors[i]);
    if (brightness !== undefined) {
      colorsByBrightness.push({
        slot: i - 1,
        color: colors[i],
        brightness
      });
    }
  }

  return colorsByBrightness;
};

export const BRIGHTNESS_THRESHOLD = 207;

// can be improved by having a first or lightest prop
// becuz right now it returns the first one that meets the threshold requirement
/*export const getLightColor = (
  colorsArray,
  brightnessThreshold = BRIGHTNESS_THRESHOLD
) => {
  for (let i = 0; i < colorsArray.length; i++) {
    if (colorsArray[i] && colorsArray[i].brightness > brightnessThreshold) {
      return colorsArray[i];
    }
  }

  return null;
};*/

// can be improved by having a first or darkest prop
// becuz right now it returns the first one that meets the threshold requirement
export const getDarkColor = (
  colorsArray: IColorBrightness[],
  brightnessThreshold = BRIGHTNESS_THRESHOLD
): IColorBrightness | undefined => {
  for (let i = 0; i < colorsArray.length; i++) {
    if (colorsArray[i] && colorsArray[i].brightness <= brightnessThreshold) {
      return colorsArray[i];
    }
  }

  return;
};

// https://css-tricks.com/snippets/javascript/lighten-darken-color/
// negative to darken, positive to lighten
export const adjustColor = (c: string, amount: number): string | undefined => {
  if (!c) return;

  let usePound = false;
  let color = c;

  if (color[0] === '#') {
    color = color.slice(1);
    usePound = true;
  }

  const num = parseInt(color, 16);

  // eslint-disable-next-line no-bitwise
  let r: string | number = (num >> 16) + amount;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  // eslint-disable-next-line no-bitwise
  let b: string | number = ((num >> 8) & 0x00ff) + amount;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  // eslint-disable-next-line no-bitwise
  let g: string | number = (num & 0x0000ff) + amount;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  [r, g, b] = [r, g, b].map(tempColor =>
    tempColor <= 15 ? `0${tempColor.toString(16)}` : tempColor.toString(16)
  );

  return (usePound ? '#' : '') + r + b + g;
};

export const isValidColor = (value: any): boolean => {
  // Reject non-string values
  if (typeof value !== 'string') return false;
  // Reject empty strings
  if (!value || !value.length) return false;
  // Assume non-hex strings are CSS colors
  if (value[0] !== '#') return true;
  // Validate hex strings
  return value.match(/^#([a-f0-9]{3}|[a-f0-9]{6})$/i) !== null;
};

interface ISchoolColor {
  primary: string;
  primaryDark: string;
}

export const getSchoolColors = (
  color1: string,
  color2: string,
  color3: string
): ISchoolColor => {
  const colorsBrightness = getColorBrightness([color1, color2, color3]);

  const darkerSchoolColor = prependHashIfNeeded(
    getDarkColor(colorsBrightness)?.color ?? THEME.brand.maxpreps
  );

  return {
    primary: darkerSchoolColor,
    primaryDark: adjustColor(darkerSchoolColor, -10) ?? darkerSchoolColor
  };
};
